/**
 * The job site — where the tech is actually going.
 *
 * In Spire the order's SHIP-TO is the service address; the bill-to can be a
 * builder, a property manager, or the contractor's own house account (FPS
 * bills plenty of installs to "The Fireplace Stop North" and ships to the
 * homeowner). Every card that tells someone where a job is must therefore
 * read the ship-to — name included, because on those orders the ship-to
 * name is the only place the real customer appears.
 */

import type { SalesOrderSummary } from './api.js';

type SiteOrder = Pick<SalesOrderSummary, 'customer' | 'shippingAddress'>;

export interface JobSite {
  /**
   * The ship-to name when it says something the customer name doesn't
   * (e.g. "Pete Murdoch" on an order billed to the store). Null when blank
   * or just a restatement of the customer.
   */
  contactName: string | null;
  /** "75 Ontario 612 · Seguin" — street + city, for one-line cards. Null when the ship-to is empty. */
  addressLine: string | null;
  /** Street, line 2, "City, ON" and postal code as separate lines, for detail views. */
  addressLines: string[];
  /** Single string for a maps query. Null when there's nothing to navigate to. */
  mapsQuery: string | null;
}

export function jobSite(order: SiteOrder): JobSite {
  const a = order.shippingAddress;
  const street = clean(a.line1);
  const city = clean(a.city);
  const cityProv = [city, clean(a.provState)].filter(isPresent).join(', ');

  const name = clean(a.name);
  const contactName = name && !sameName(name, order.customer.name) ? name : null;

  const addressLines = [street, clean(a.line2), cityProv || null, clean(a.postalCode)].filter(isPresent);
  const oneLine = [street, city].filter(isPresent);

  return {
    contactName,
    addressLine: oneLine.length > 0 ? oneLine.join(' · ') : null,
    addressLines,
    mapsQuery: addressLines.length > 0 ? addressLines.join(', ') : null,
  };
}

/**
 * One line for the tightest cards: "Pete Murdoch · 75 Ontario 612 · Seguin".
 * Null when the ship-to adds nothing.
 */
export function jobSiteLine(order: SiteOrder): string | null {
  const site = jobSite(order);
  const parts = [site.contactName, site.addressLine].filter(isPresent);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function clean(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isPresent(value: string | null): value is string {
  return value !== null;
}

/** "Kessler, Jolene" vs "Jolene Kessler" vs "KESSLER JOLENE" are the same person. */
function sameName(a: string, b: string): boolean {
  const key = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean)
      .sort()
      .join(' ');
  return key(a) === key(b);
}
